"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import { Autoplay, EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { StartIcon, StopSign, transitionTime } from "ui"
import {
  extractProperty,
  getLinkHrefFromMultiUrlPickerItem,
  getUmbracoMediaUrl,
  replaceRichTextLinks,
} from "utils"
import { useTypingAnimation } from "../hooks/useTypingAnimation"
import { useAnchorScroll } from "../useAnchorScroll"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Styled Components (mobile-first, breakpoints.up only) ---

const Section = styled.section`
  display: flex;
  flex-direction: column;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  position: relative;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    height: 100svh;
    overflow: hidden;
  }
`

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 74px 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 24px 20px 32px;
    width: 720px;
    flex-shrink: 0;
    padding: 64px;
    padding-inline-start: 120px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    flex: 1;
    width: auto;
    padding-inline-start: 240px;
  }
`

const LogoArea = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  img {
    width: 134px;
    height: 82px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-wrap: nowrap;
    gap: 24px;
    margin-bottom: 30px;

    img {
      width: 157px;
      height: 97px;
    }
  }
`

const Headline = styled.h1`
  color: var(--comp-heading);
  margin: 0;
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.8px;
  position: relative;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 72px;
    letter-spacing: 0;
  }
`

const HeadlinePlaceholder = styled.span`
  display: block;
  visibility: hidden;
`

const HeadlineTyped = styled.span`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
`

const BodyText = styled.div`
  color: var(--comp-text);
  font-size: 18px;
  font-weight: 400;
  line-height: 1.3;
  margin-top: 8px;
  margin-bottom: 24px;

  p {
    margin: 0;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    line-height: 1.5;
    letter-spacing: -0.4px;
    margin-top: 8px;
    margin-bottom: 40px;
  }
`

const ButtonsRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: max-content;
    gap: 24px;
  }
`

const CtaButton = styled(Button)`
  && {
    width: 155px;
    height: 40px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
    text-transform: none;
    background-color: var(--inner-bg);
    color: var(--inner-text);

    &:hover {
      background-color: var(--inner-bg);
      opacity: 0.9;
    }

    ${(props) => props.theme.breakpoints.up("md")} {
      width: auto;
      height: 48px;
      flex: 1;
      font-size: 20px;
    }
  }
`

const SecondaryButton = styled(Button)`
  && {
    width: 155px;
    height: 40px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
    text-transform: none;
    background-color: transparent;
    color: var(--inner-bg);
    border: 2px solid var(--inner-bg);

    &:hover {
      background-color: transparent;
      opacity: 0.9;
    }

    ${(props) => props.theme.breakpoints.up("md")} {
      width: auto;
      height: 48px;
      flex: 1;
      font-size: 20px;
    }
  }
`

const MediaArea = styled.div`
  position: relative;
  width: 100%;
  height: 341px;
  display: flex;
  order: 2;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-shrink: 0;
    flex-basis: 50%;
    min-width: 0;
    height: 100%;
    order: unset;
  }

  .swiper {
    width: 100%;
    height: 100%;
    padding-block: 0;
  }

  .swiper-slide {
    position: relative;
    width: 100%;
    height: 100%;
    opacity: 0 !important;
  }

  .swiper-slide-active {
    opacity: 1 !important;
  }
`

const PlayPauseBtn = styled.button`
  position: absolute;
  bottom: 20px;
  inset-inline-end: 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    inset-inline-end: 120px;
  }
  z-index: 10;
  background: rgba(255, 255, 255, 0.4);
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

const VideoPlayPauseBtn = styled.button`
  position: absolute;
  bottom: 20px;
  inset-inline-end: 20px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.4);
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

// --- Sub-components ---

function SingleMedia({ item }: { item: any }) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const url = item?.url || ""
  const isVideo = url.endsWith(".mp4") || url.endsWith(".webm")
  const videoRef = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(false)

  if (isVideo) {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <video
          ref={videoRef}
          src={getUmbracoMediaUrl(url)}
          autoPlay
          muted
          loop
          playsInline
          onPlay={() => setPaused(false)}
          onPause={() => setPaused(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <VideoPlayPauseBtn
          onClick={() => {
            if (videoRef.current) {
              analytics.mediaClick(paused ? "play" : "pause", LANDING_ELEMENTS.hero)
              if (paused) videoRef.current.play()
              else videoRef.current.pause()
            }
          }}
          aria-label={
            paused ? (isRtl ? "הפעל וידאו" : "Play video") : isRtl ? "עצור וידאו" : "Pause video"
          }
        >
          {paused ? <StartIcon /> : <StopSign />}
        </VideoPlayPauseBtn>
      </div>
    )
  }

  return (
    <Image
      src={getUmbracoMediaUrl(url)}
      fill
      alt={item?.altText || ""}
      style={{ objectFit: "cover" }}
    />
  )
}

// --- Main Component ---

function HeroOption3({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const logos = extractProperty(data, "heroLogos", true) as any[] | undefined
  const headline = extractProperty(data, "heroHeadline") as string | undefined
  const bodyText = extractProperty(data, "heroBodyText") as string | undefined
  const ctaButton = extractProperty(data, "heroCtaButton") as any[] | undefined
  const secondaryBtn = extractProperty(data, "heroSecondaryButton") as any[] | undefined
  const mediaItems = extractProperty(data, "heroBackgroundMedia", true) as any[] | undefined

  const isRtl = locale === "he" || !locale
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const { ref: headlineRef, inView: headlineInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  })
  const { displayed: typedHeadline, full: fullHeadline } = useTypingAnimation(
    headline,
    headlineInView,
  )

  const hasMedia = Array.isArray(mediaItems) && mediaItems.length > 0
  const hasMultipleMedia = hasMedia && mediaItems!.length > 1
  const showPlayPause = hasMultipleMedia

  const togglePlayPause = useCallback(() => {
    if (swiperRef) {
      if (isPlaying) {
        swiperRef.autoplay.stop()
      } else {
        swiperRef.autoplay.start()
      }
    }
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.hero)
    setIsPlaying(!isPlaying)
  }, [swiperRef, isPlaying, analytics])

  useEffect(() => {
    if (!containerRef.current || !hasMultipleMedia) return
    const slides = containerRef.current.querySelectorAll(".swiper-slide")
    slides.forEach((slide, i) => {
      if (i === activeIndex) {
        slide.removeAttribute("inert")
        slide.removeAttribute("aria-hidden")
      } else {
        slide.setAttribute("inert", "")
        slide.setAttribute("aria-hidden", "true")
      }
    })
  }, [activeIndex, hasMultipleMedia])

  const ctaLink = ctaButton?.[0]
  const secondaryLink = secondaryBtn?.[0]
  const ctaHref = ctaLink ? getLinkHrefFromMultiUrlPickerItem(ctaLink as any) : undefined
  const secondaryHref = secondaryLink
    ? getLinkHrefFromMultiUrlPickerItem(secondaryLink as any)
    : undefined
  const handleAnchorClick = useAnchorScroll()
  const logoItems = Array.isArray(logos) ? logos : []

  return (
    <Section>
      {/* Content area — first in JSX = right side in RTL row-reverse */}
      <ContentArea>
        {logoItems.length > 0 && (
          <LogoArea>
            {logoItems.map((logo: any, i: number) => (
              <Image
                key={i}
                src={getUmbracoMediaUrl(logo?.url || "/")}
                width={157}
                height={97}
                alt={logo?.altText || ""}
                style={{ objectFit: "contain" }}
              />
            ))}
          </LogoArea>
        )}

        {headline && (
          <Headline ref={headlineRef}>
            <HeadlinePlaceholder>{fullHeadline}</HeadlinePlaceholder>
            <HeadlineTyped>{typedHeadline}</HeadlineTyped>
          </Headline>
        )}

        {bodyText && (
          <BodyText
            dangerouslySetInnerHTML={{
              __html: replaceRichTextLinks(bodyText, true),
            }}
          />
        )}

        {(ctaLink || secondaryLink) && (
          <ButtonsRow>
            {ctaLink && (
              <CtaButton
                variant="contained"
                href={ctaHref}
                onClick={(e) => {
                  analytics.ctaClick(ctaLink.name, LANDING_ELEMENTS.hero)
                  handleAnchorClick(e, ctaHref)
                }}
                disableElevation
              >
                {ctaLink.name}
              </CtaButton>
            )}
            {secondaryLink && (
              <SecondaryButton
                variant="outlined"
                href={secondaryHref}
                onClick={(e) => {
                  analytics.ctaClick(secondaryLink.name, LANDING_ELEMENTS.hero)
                  handleAnchorClick(e, secondaryHref)
                }}
              >
                {secondaryLink.name}
              </SecondaryButton>
            )}
          </ButtonsRow>
        )}
      </ContentArea>

      {/* Media area — second in JSX = left side in RTL row-reverse */}
      {hasMedia && (
        <MediaArea ref={containerRef}>
          {mediaItems!.length === 1 ? (
            <SingleMedia item={mediaItems![0]} />
          ) : (
            <Swiper
              loop
              onSwiper={setSwiperRef}
              onSlideChange={(swiper) => {
                setActiveIndex(swiper.realIndex)
                const activeSlide = swiper.slides[swiper.activeIndex]
                const video = activeSlide?.querySelector("video")
                if (video) {
                  video.currentTime = 0
                  video.play()
                }
              }}
              modules={[EffectFade, Autoplay]}
              effect="fade"
              speed={transitionTime * 1000}
              style={{ width: "100%", height: "100%" }}
              autoplay={
                isPlaying
                  ? {
                      delay: 4500,
                      disableOnInteraction: false,
                    }
                  : false
              }
            >
              {mediaItems!.map((item: any, i: number) => (
                <SwiperSlide key={i}>
                  <SingleMedia item={item} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          {showPlayPause && (
            <PlayPauseBtn
              onClick={togglePlayPause}
              aria-label={
                isPlaying
                  ? isRtl
                    ? "עצור תנועת הרכיב"
                    : "Stop animation"
                  : isRtl
                  ? "הפעל תנועה רכיב"
                  : "Play animation"
              }
            >
              {isPlaying ? <StopSign /> : <StartIcon />}
            </PlayPauseBtn>
          )}
        </MediaArea>
      )}
    </Section>
  )
}

export default HeroOption3
